import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft,
  Package,
  Users,
  History,
  Rocket,
  Check,
  X,
  Clock,
  Edit2,
  FileCode,
  Globe,
  Building2,
} from "lucide-react";

export default function AppDetailsPage() {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [appDetails, setAppDetails] = useState(null);

  useEffect(() => {
    fetchAppDetails();
  }, [appId]);

  const fetchAppDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/apps/${encodeURIComponent(appId)}/details`);
      setAppDetails(response.data);
    } catch (error) {
      console.error("Failed to fetch app details:", error);
      toast.error("Failed to load application details");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-transparent flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!appDetails) {
    return (
      <div className="min-h-screen bg-transparent p-6">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back
        </Button>
        <Card>
          <CardContent className="p-8 text-center text-white/80">
            Application not found
          </CardContent>
        </Card>
      </div>
    );
  }

  const { app_info, version_history, deployment_history, packaging_info } = appDetails;

  return (
    <div className="min-h-screen bg-transparent">
      {/* Header */}
      <div className="border-b bg-[rgba(229,239,250,0.2)] backdrop-blur-[5px]">
        <div className="container mx-auto px-6 py-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-3">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="h-16 w-16 rounded-lg bg-white/15 flex items-center justify-center overflow-hidden">
                {app_info?.icon ? (
                  <img 
                    src={app_info.icon} 
                    alt={app_info?.name || appId}
                    className="h-14 w-14 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                ) : null}
                <Package 
                  className="h-8 w-8 text-white/80" 
                  style={{ display: app_info?.icon ? 'none' : 'block' }}
                />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">{app_info?.name || appId}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant={appDetails.status === "Managed" ? "default" : "secondary"}>
                    {appDetails.status}
                  </Badge>
                  <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">{appDetails.source}</Badge>
                  {app_info?.publisher && (
                    <span className="text-sm text-white/80 flex items-center gap-1">
                      <Building2 className="h-3 w-3" /> {app_info.publisher}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="text-right text-sm text-white/80">
              <div>{appDetails.total_packages} package(s)</div>
              <div>{appDetails.total_deployments} deployment(s)</div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-6 space-y-6">
        {/* Governance - Link to unified governance page */}
        <Card className="bg-blue-500/5 border-blue-500/20">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-blue-400" />
                <div>
                  <div className="font-medium text-white">App Governance</div>
                  <div className="text-sm text-white/80">
                    Manage owner, testers, and notes for this application
                  </div>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={() => {
                  const appName = app_info?.name || app_info?.app_name || appId;
                  navigate(`/discover/governance/${encodeURIComponent(appName)}?from=apps`);
                }}
                className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
              >
                <Edit2 className="h-4 w-4 mr-2" /> 
                Open Governance
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Version History */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Version History</CardTitle>
            </div>
            <CardDescription>All packaged versions of this application</CardDescription>
          </CardHeader>
          <CardContent>
            {version_history?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-white font-semibold">Version</TableHead>
                    <TableHead className="text-white font-semibold">Source</TableHead>
                    <TableHead className="text-white font-semibold">Packaged</TableHead>
                    <TableHead className="text-white font-semibold">Deployed</TableHead>
                    <TableHead className="text-white font-semibold">Status</TableHead>
                    <TableHead className="text-white font-semibold">Type</TableHead>
                    <TableHead className="text-white font-semibold">Dependencies</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {version_history.map((ver, i) => (
                    <TableRow key={i} className={ver.status === "Current" ? "bg-green-500/10" : ""}>
                      <TableCell className="font-medium text-white">{ver.version}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">{ver.source}</Badge>
                      </TableCell>
                      <TableCell>
                        {ver.packaged ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <X className="h-4 w-4 text-white/40" />
                        )}
                      </TableCell>
                      <TableCell>
                        {ver.deployed ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <X className="h-4 w-4 text-white/40" />
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          ver.status === "Current" ? "default" :
                          ver.status === "Superseded" ? "secondary" :
                          "outline"
                        }>
                          {ver.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs uppercase bg-secondary/50 text-white border-border">
                          {ver.installer_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-white">
                        {ver.dependencies?.length > 0 ? (
                          <span className="text-xs">{ver.dependencies.length} dep(s)</span>
                        ) : (
                          <span className="text-white/50">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-white/80">
                No versions packaged yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* Deployment History */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Rocket className="h-5 w-5 text-primary" />
              <CardTitle className="text-lg">Deployment History</CardTitle>
            </div>
            <CardDescription>Where this application has been deployed</CardDescription>
          </CardHeader>
          <CardContent>
            {deployment_history?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-white font-semibold">Deployment</TableHead>
                    <TableHead className="text-white font-semibold">Version</TableHead>
                    <TableHead className="text-white font-semibold">Intune App ID</TableHead>
                    <TableHead className="text-white font-semibold">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deployment_history.map((dep, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-medium text-white">{dep.deployment_name}</TableCell>
                      <TableCell className="text-white">{dep.version}</TableCell>
                      <TableCell className="font-mono text-xs text-white/80">{dep.intune_app_id}</TableCell>
                      <TableCell>
                        <Badge variant={dep.status === "Active" ? "default" : "secondary"}>
                          {dep.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-white/50">
                No deployments yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* Current Packaging & Dependencies */}
        {packaging_info && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <FileCode className="h-5 w-5 text-white/80" />
                <CardTitle className="text-lg">Current Packaging Configuration</CardTitle>
              </div>
              <CardDescription>Active version: {packaging_info.version}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div>
                  <div className="text-xs text-white/80 uppercase tracking-wide mb-1">Installer Type</div>
                  <Badge variant="outline" className="uppercase">{packaging_info.installer_type}</Badge>
                </div>
                <div>
                  <div className="text-xs text-white/80 uppercase tracking-wide mb-1">Silent Args</div>
                  <code className="text-sm bg-white/15 px-2 py-1 rounded">{packaging_info.silent_args || "N/A"}</code>
                </div>
                <div>
                  <div className="text-xs text-white/80 uppercase tracking-wide mb-1">Package Format</div>
                  <Badge variant="outline" className="uppercase">{packaging_info.package_format}</Badge>
                </div>
                <div>
                  <div className="text-xs text-white/80 uppercase tracking-wide mb-1">Auto-Uninstall Previous</div>
                  <Badge variant={packaging_info.auto_uninstall_enabled ? "default" : "secondary"}>
                    {packaging_info.auto_uninstall_enabled ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
              </div>

              {/* Dependencies */}
              {packaging_info.dependencies?.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <div className="text-sm font-medium mb-3">Dependencies ({packaging_info.dependencies.length})</div>
                  <div className="space-y-2">
                    {packaging_info.dependencies.map((dep, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-white/10 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Package className="h-4 w-4 text-blue-600" />
                          <span className="font-medium">{dep.package_id}</span>
                          {dep.min_version && (
                            <span className="text-xs text-white/80">(min: {dep.min_version})</span>
                          )}
                        </div>
                        <Badge variant="outline" className="text-xs">DEPENDENCY</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* MSI Properties if applicable */}
              {packaging_info.installer_type === "msi" && packaging_info.msi_properties && Object.keys(packaging_info.msi_properties).length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <div className="text-sm font-medium mb-3">MSI Properties</div>
                  <div className="bg-white/10 rounded-lg p-3">
                    <pre className="text-xs overflow-x-auto">
                      {JSON.stringify(packaging_info.msi_properties, null, 2)}
                    </pre>
                  </div>
                </div>
              )}

              {/* MSI Shortcuts removed on install (Phase 1 — Option E) */}
              {packaging_info.installer_type === "msi" && packaging_info.msi_shortcut_removals?.length > 0 && (
                <div
                  className="mt-6 pt-4 border-t"
                  data-testid="packaging-msi-shortcut-removals-section"
                >
                  <div className="text-sm font-medium mb-1 text-white">
                    🪦 MSI Shortcuts Removed on Install
                    <Badge variant="outline" className="ml-2 text-xs">
                      {packaging_info.msi_shortcut_removals.length}
                    </Badge>
                  </div>
                  <div className="text-xs text-white/70 mb-3">
                    The PSADT post-install phase removes each .lnk at the exact location
                    reported by the MSI (per-user/all-users Desktop or the declared Start
                    Menu sub-folder), and tidies up any empty parent folders it leaves behind.
                  </div>
                  <div className="bg-white/10 rounded-lg p-3">
                    <ul className="text-xs space-y-1">
                      {packaging_info.msi_shortcut_removals.map((entry, i) => {
                        // Entries are either rich dicts (new wizard) or
                        // bare strings (legacy). Render the same row layout
                        // either way and append a short location hint when
                        // it's available.
                        let label;
                        if (entry && typeof entry === "object") {
                          const loc = entry.is_desktop
                            ? entry.desktop_scope === "user"
                              ? "user Desktop"
                              : "all-users Desktop"
                            : entry.start_menu_subpath
                            ? `Start Menu\\${entry.start_menu_subpath}`
                            : "Start Menu";
                          label = `${entry.name} — ${loc}`;
                        } else {
                          label = String(entry);
                        }
                        return (
                          <li
                            key={i}
                            className="font-mono"
                            data-testid={`msi-shortcut-removal-row-${i}`}
                          >
                            • {label}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              )}

              {/* Expert Mode — Custom Files (Copy-ADTFile / Copy-ADTFileToUserProfiles) */}
              {packaging_info.custom_files?.length > 0 && (
                <div className="mt-6 pt-4 border-t" data-testid="packaging-custom-files-section">
                  <div className="text-sm font-medium mb-1 text-white">
                    Expert Mode — Custom Files
                    <Badge variant="outline" className="ml-2 text-xs">
                      {packaging_info.custom_files.length}
                    </Badge>
                  </div>
                  <div className="text-xs text-white/70 mb-3">
                    Files copied at runtime via Copy-ADTFile / Copy-ADTFileToUserProfiles.
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-white font-semibold">Filename</TableHead>
                        <TableHead className="text-white font-semibold">Destination</TableHead>
                        <TableHead className="text-white font-semibold">Method</TableHead>
                        <TableHead className="text-white font-semibold">Phase</TableHead>
                        <TableHead className="text-white font-semibold">Remove on uninstall</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {packaging_info.custom_files.map((cf, i) => {
                        const method = cf.copy_method === "user_profiles"
                          ? "Copy-ADTFileToUserProfiles"
                          : "Copy-ADTFile";
                        const destBase = cf.copy_method === "user_profiles"
                          ? "(each user profile)"
                          : (cf.destination === "__custom__" ? (cf.customPath || cf.custom_path || "") : (cf.destination || ""));
                        const sub = (cf.subpath || "").replace(/^[\\/]+/, "");
                        const fullDest = destBase
                          ? (sub ? `${destBase.replace(/[\\/]+$/, "")}\\${sub}` : destBase)
                          : "—";
                        const phase = (cf.phase || "post_install")
                          .replace(/_/g, "-")
                          .replace(/\b\w/g, (c) => c.toUpperCase());
                        return (
                          <TableRow key={i}>
                            <TableCell className="font-mono text-xs text-white">{cf.filename || "—"}</TableCell>
                            <TableCell className="font-mono text-xs text-white/90 break-all">{fullDest}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-[10px] font-mono">{method}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-xs">{phase}</Badge>
                            </TableCell>
                            <TableCell>
                              {cf.remove_on_uninstall ? (
                                <Check className="h-4 w-4 text-green-500" />
                              ) : (
                                <X className="h-4 w-4 text-white/40" />
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Expert Mode — Registry Changes */}
              {packaging_info.registry_entries?.length > 0 && (
                <div className="mt-6 pt-4 border-t" data-testid="packaging-registry-entries-section">
                  <div className="text-sm font-medium mb-1 text-white">
                    Expert Mode — Registry Changes
                    <Badge variant="outline" className="ml-2 text-xs">
                      {packaging_info.registry_entries.length}
                    </Badge>
                  </div>
                  <div className="text-xs text-white/70 mb-3">
                    Registry values applied via Set-ADTRegistryKey / Remove-ADTRegistryKey.
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-white font-semibold">Action</TableHead>
                        <TableHead className="text-white font-semibold">Key</TableHead>
                        <TableHead className="text-white font-semibold">Value name</TableHead>
                        <TableHead className="text-white font-semibold">Type</TableHead>
                        <TableHead className="text-white font-semibold">Value</TableHead>
                        <TableHead className="text-white font-semibold">Phase</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {packaging_info.registry_entries.map((r, i) => {
                        const action = (r.action || "set").toUpperCase();
                        const hive = (r.hive || "HKLM").toUpperCase();
                        const keyPath = (r.key || "").replace(/^\\+/, "");
                        const fullKey = keyPath ? `${hive}:\\${keyPath}` : hive;
                        const phase = (r.phase || "post_install")
                          .replace(/_/g, "-")
                          .replace(/\b\w/g, (c) => c.toUpperCase());
                        const rawValue = r.value ?? "";
                        const valueDisplay = action === "REMOVE"
                          ? "—"
                          : (typeof rawValue === "string" && rawValue.length > 60
                              ? `${rawValue.slice(0, 60)}…`
                              : (rawValue === "" ? "(empty)" : String(rawValue)));
                        return (
                          <TableRow key={i}>
                            <TableCell>
                              <Badge variant={action === "REMOVE" ? "destructive" : "default"} className="text-xs">
                                {action}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs text-white/90 break-all">{fullKey}</TableCell>
                            <TableCell className="font-mono text-xs text-white">{r.name || "—"}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{r.type || "—"}</Badge>
                            </TableCell>
                            <TableCell className="font-mono text-xs text-white/90 break-all">{valueDisplay}</TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-xs">{phase}</Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
